/**
 * Relay Client — Direct connection to Solidafy Query Relay
 *
 * Executes SQL and DSL queries directly against the relay service.
 * Credentials are fetched from the portal API and cached.
 */
import { normalizeSql } from './sql-utils';
import type { ChartSpec } from '@/types/dashboard';
import type { LlmResult } from '@/types/explorer';
import type { TraceTree } from '@/types/trace';
import { api } from '@/lib/api';
import { edenError } from '@solidafy/api-client';

let cachedCredentials: { relay_url: string; api_key: string } | null = null;
let credentialsFetchPromise: Promise<{ relay_url: string; api_key: string }> | null = null;

export type RelayQueryResult = {
  columns: string[];
  rows: Record<string, unknown>[];
  rowCount: number;
  error?: string;
  trace_tree?: TraceTree;
  llm?: LlmResult;
};

/** Shape of the raw JSON returned by the relay /query endpoint. */
type RelayRawResponse = {
  columns?: string[];
  rows?: Record<string, unknown>[];
  rowCount?: number;
  error?: string;
  trace_tree?: TraceTree;
  chart?: Record<string, unknown>;
  llm?: LlmResult;
};

function isDsl(code: string): boolean {
  const trimmed = code.trim();
  return (
    trimmed.startsWith('source(') ||
    trimmed.includes('.since(') ||
    trimmed.includes('.chart(') ||
    trimmed.includes('.llm(') ||
    trimmed.includes('.schema()') ||
    trimmed.includes('.trace_tree(')
  );
}

async function getRelayCredentials(): Promise<{ relay_url: string; api_key: string }> {
  if (cachedCredentials) return cachedCredentials;
  if (credentialsFetchPromise) return credentialsFetchPromise;

  credentialsFetchPromise = (async () => {
    const { data, error } = await api.api.v1.flight.credentials.get();

    if (error) {
      throw new Error(edenError(error, 'Failed to get relay credentials'));
    }

    cachedCredentials = { relay_url: data.relay_url, api_key: data.api_key };
    return cachedCredentials;
  })();

  try {
    return await credentialsFetchPromise;
  } finally {
    credentialsFetchPromise = null;
  }
}

export function clearRelayCredentials(): void {
  cachedCredentials = null;
  credentialsFetchPromise = null;
}

export async function executeRelayQuery(sql: string, options?: { signal?: AbortSignal }): Promise<RelayQueryResult> {
  const credentials = await getRelayCredentials();
  const normalizedSql = normalizeSql(sql);

  const response = await fetch(`${credentials.relay_url}/query`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': credentials.api_key,
    },
    body: JSON.stringify({ sql: normalizedSql }),
    signal: options?.signal,
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Query failed: ${response.status} ${text}`);
  }

  const data = (await response.json()) as RelayRawResponse;
  if (data.error) throw new Error(data.error);

  return {
    columns: data.columns ?? [],
    rows: data.rows ?? [],
    rowCount: data.rowCount ?? data.rows?.length ?? 0,
    trace_tree: data.trace_tree,
  };
}

export async function executeRelayDsl(
  dsl: string,
  options?: { signal?: AbortSignal },
): Promise<RelayQueryResult & { chart?: Record<string, unknown> }> {
  const credentials = await getRelayCredentials();
  const normalizedDsl = normalizeSql(dsl);

  const response = await fetch(`${credentials.relay_url}/query`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': credentials.api_key,
    },
    body: JSON.stringify({ sql: normalizedDsl }),
    signal: options?.signal,
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`DSL query failed: ${response.status} ${text}`);
  }

  const data = (await response.json()) as RelayRawResponse;
  if (data.error) throw new Error(data.error);

  return {
    columns: data.columns ?? [],
    rows: data.rows ?? [],
    rowCount: data.rowCount ?? data.rows?.length ?? 0,
    chart: data.chart,
    trace_tree: data.trace_tree,
    llm: data.llm,
  };
}

export async function executeRelayCode(code: string, options?: { signal?: AbortSignal }): Promise<RelayQueryResult> {
  return isDsl(code) ? executeRelayDsl(code, options) : executeRelayQuery(code, options);
}

/**
 * Execute DSL with .output() — writes data to catalog via relay /execute endpoint.
 * Unlike /query (read-only) and /materialize (caching only), /execute handles
 * arbitrary DSL including mock() and .output() for actual data writes.
 */
export async function executeRelayWrite(dsl: string, options?: { signal?: AbortSignal }): Promise<{ error?: string }> {
  const credentials = await getRelayCredentials();

  const response = await fetch(`${credentials.relay_url}/query`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': credentials.api_key,
    },
    body: JSON.stringify({ sql: dsl }),
    signal: options?.signal,
  });

  if (!response.ok) {
    const text = await response.text();
    return { error: `Execute failed: ${response.status} ${text}` };
  }

  const data = (await response.json()) as RelayRawResponse;
  if (data.error) return { error: data.error };
  return {};
}

// -- AI Chat --

export type AiContentBlock = { type: 'text'; text: string };
export type AiMessage = { role: 'user' | 'assistant'; content: string };
export type AiMode = 'query' | 'pipeline' | 'chart';

export type QueryResult = {
  dsl: string;
  columns: string[];
  rows: Record<string, unknown>[];
};

export type AiResponse = {
  answer: string;
  dsl?: string;
  columns?: string[];
  rows?: Record<string, unknown>[];
  rowCount?: number;
  chart?: ChartSpec;
  pipeline?: string;
  error?: string;
  nextQuestions?: string[];
  queryResults?: QueryResult[];
};

export type AiRequestOptions = {
  signal?: AbortSignal;
  mode?: AiMode;
  context?: Record<string, unknown>;
};

export type AiStreamEvent =
  | { type: 'turn_start'; turn: number; max_turns: number; mode: string }
  | { type: 'thinking'; turn: number }
  | { type: 'tool_start'; tool: string; input?: string }
  | { type: 'tool_result'; tool: string; success: boolean; summary: string }
  | { type: 'gpt_response'; response: string; ms: number; contextTokens?: number }
  | {
      type: 'query_result';
      index: number;
      success: boolean;
      dsl: string;
      columns?: string[];
      rows?: Record<string, unknown>[];
      error?: string;
    }
  | { type: 'summary_chunk'; text: string }
  | { type: 'complete'; data: AiResponse }
  | { type: 'error'; message: string };

export type AiStreamCallbacks = {
  onEvent: (event: AiStreamEvent) => void;
  onComplete: (response: AiResponse) => void;
  onError: (error: string) => void;
  onSummaryChunk?: (text: string) => void;
};

export async function executeRelayAi(
  message: string,
  history: AiMessage[],
  options?: AiRequestOptions,
): Promise<AiResponse> {
  const credentials = await getRelayCredentials();

  const body: Record<string, unknown> = {
    message,
    history: history.map((m) => ({
      role: m.role,
      content: [{ type: 'text' as const, text: m.content || '' }],
    })),
  };
  if (options?.mode && options.mode !== 'query') {
    body.mode = options.mode;
  }
  if (options?.context) {
    body.context = options.context;
  }

  const response = await fetch(`${credentials.relay_url}/ai`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': credentials.api_key,
    },
    body: JSON.stringify(body),
    signal: options?.signal,
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`AI request failed: ${response.status} ${text}`);
  }

  return (await response.json()) as AiResponse;
}

export async function executeRelayAiStream(
  message: string,
  history: AiMessage[],
  callbacks: AiStreamCallbacks,
  options?: AiRequestOptions,
): Promise<void> {
  // Use the new /chat endpoint which handles semantic search, entity validation,
  // DSL generation, execution, and summarization server-side.
  return executeRelayChatStream(message, history, callbacks, options);
}

/**
 * Stream SSE from the relay /chat endpoint.
 *
 * SSE events:
 *   step     — progress updates (step number, status, label)
 *   search   — semantic search results
 *   validate — entity validation results
 *   gpt      — LLM response with DSL
 *   exec     — query execution results (rows, success/failure)
 *   summary  — natural language summary
 *   done     — total pipeline time
 *   error    — if something fails
 */
type ChatStepPayload = { step: number; status: string; label: string };
type ChatExecResult = { dsl: string; ok: boolean; output: string; rows: Record<string, unknown>[] };
type ChatExecPayload = { results: ChatExecResult[]; ms: number };
type ChatGptPayload = { response: string; ms: number; context_tokens?: number };
type ChatSummaryPayload = { text: string; ms: number };
type ChatDonePayload = { total_ms: number };
type ChatSearchPayload = { results: Record<string, unknown>[]; ms: number };
type ChatValidatePayload = { validations: Record<string, unknown>[] };

async function executeRelayChatStream(
  message: string,
  history: AiMessage[],
  callbacks: AiStreamCallbacks,
  options?: AiRequestOptions,
): Promise<void> {
  const credentials = await getRelayCredentials();

  const body: Record<string, unknown> = {
    message,
    ...((options?.context as Record<string, unknown>)?.namespace
      ? { namespace: (options?.context as Record<string, unknown>).namespace }
      : {}),
    history: history.map((m) => ({ role: m.role, content: m.content })),
  };

  const response = await fetch(`${credentials.relay_url}/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': credentials.api_key,
    },
    body: JSON.stringify(body),
    signal: options?.signal,
  });

  if (!response.ok) {
    const text = await response.text();
    callbacks.onError(`Chat request failed: ${response.status} ${text}`);
    return;
  }

  if (!response.body) {
    callbacks.onError('No response body');
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  // Accumulate response parts across SSE events
  let gptResponse = '';
  let summaryText = '';
  let execResults: ChatExecResult[] = [];
  let dsl = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const parts = buffer.split('\n\n');
    buffer = parts.pop()!;

    for (const part of parts) {
      if (!part.trim()) continue;
      let eventType = '';
      let data = '';
      for (const line of part.split('\n')) {
        if (line.startsWith('event:')) eventType = line.slice(6).trim();
        else if (line.startsWith('data:')) data = line.slice(5).trim();
      }

      if (!eventType || !data) continue;

      try {
        const raw = JSON.parse(data) as { event?: string; data?: Record<string, unknown> } & Record<string, unknown>;
        // Relay wraps payload in { event, data } — unwrap if nested
        const parsed = raw.data ?? raw;

        switch (eventType) {
          case 'step': {
            const step = parsed as unknown as ChatStepPayload;
            // Map step events to tool_start/tool_result for the progress UI
            if (step.status === 'running') {
              callbacks.onEvent({ type: 'tool_start', tool: `Step ${step.step}`, input: step.label });
            } else if (step.status === 'done' || step.status === 'skipped') {
              callbacks.onEvent({
                type: 'tool_result',
                tool: `Step ${step.step}`,
                success: step.status === 'done',
                summary: step.label,
              });
            }
            break;
          }
          case 'search': {
            const search = parsed as unknown as ChatSearchPayload;
            callbacks.onEvent({
              type: 'tool_result',
              tool: 'search',
              success: true,
              summary: `${search.results?.length ?? 0} context matches (${search.ms}ms)`,
            });
            break;
          }
          case 'validate': {
            const validate = parsed as unknown as ChatValidatePayload;
            const count = validate.validations?.length ?? 0;
            if (count > 0) {
              callbacks.onEvent({
                type: 'tool_result',
                tool: 'validate',
                success: true,
                summary: `${count} entities validated`,
              });
            }
            break;
          }
          case 'gpt': {
            const gpt = parsed as unknown as ChatGptPayload;
            gptResponse = gpt.response ?? '';
            // Extract DSL code blocks from the GPT response
            const dslMatch = gptResponse.match(/```(?:sql|dsl)?\s*\n([\s\S]*?)```/);
            if (dslMatch) {
              dsl = dslMatch[1].trim();
            }
            callbacks.onEvent({
              type: 'gpt_response',
              response: gptResponse,
              ms: gpt.ms ?? 0,
              contextTokens: gpt.context_tokens,
            });
            break;
          }
          case 'exec': {
            const exec = parsed as unknown as ChatExecPayload;
            execResults = exec.results ?? [];
            // Emit a query_result event per query with full DSL + data
            for (let qi = 0; qi < execResults.length; qi++) {
              const r = execResults[qi];
              callbacks.onEvent({
                type: 'query_result',
                index: qi,
                success: r.ok,
                dsl: r.dsl,
                columns: r.ok && r.rows?.[0] ? Object.keys(r.rows[0]) : undefined,
                rows: r.ok ? r.rows : undefined,
                error: r.ok ? undefined : r.output,
              });
            }
            const totalRows = execResults.reduce((sum, r) => sum + (r.ok ? (r.rows?.length ?? 0) : 0), 0);
            const failed = execResults.filter((r) => !r.ok).length;
            callbacks.onEvent({
              type: 'tool_result',
              tool: 'execute',
              success: failed === 0,
              summary:
                failed > 0
                  ? `${failed}/${execResults.length} query(s) failed (${exec.ms}ms)`
                  : `${totalRows} rows from ${execResults.length} query(s) (${exec.ms}ms)`,
            });
            break;
          }
          case 'summary_chunk': {
            const chunk = (parsed as unknown as { text: string }).text ?? '';
            summaryText += chunk;
            callbacks.onEvent({ type: 'summary_chunk', text: summaryText });
            callbacks.onSummaryChunk?.(summaryText);
            break;
          }
          case 'summary': {
            const summary = parsed as unknown as ChatSummaryPayload;
            summaryText = summary.text ?? '';
            break;
          }
          case 'done': {
            const doneParsed = parsed as unknown as ChatDonePayload;
            // Build per-query results for separate table rendering
            const successResults = execResults.filter((r) => r.ok && r.rows?.length > 0);
            const queryResults: { dsl: string; columns: string[]; rows: Record<string, unknown>[] }[] =
              successResults.map((r) => ({
                dsl: r.dsl,
                columns: r.rows?.[0] ? Object.keys(r.rows[0]) : [],
                rows: r.rows ?? [],
              }));

            // For backwards compat, also set top-level columns/rows from first result
            const firstResult = queryResults[0];
            let answer = summaryText || gptResponse;

            // Parse follow-up questions — search both GPT and summary text
            let nextQuestions: string[] | undefined;
            // Match any variation: **Next questions:**, Next questions:, Follow-up:, etc.
            const nqStripRegex = /\n*\*{0,2}(?:Next questions|Follow[- ]?up(?:\s+questions)?)[*:]*\s*\n[\s\S]*/i;
            for (const text of [gptResponse, summaryText]) {
              if (!text) continue;
              const headerIdx = text.search(/\*{0,2}(?:Next questions|Follow[- ]?up(?:\s+questions)?)[*:]/i);
              if (headerIdx === -1) continue;
              const afterHeader = text.slice(headerIdx);
              const parsed = afterHeader
                .split('\n')
                .slice(1) // skip the header line itself
                .filter((l: string) => /^\s*[-*]|^\s*Q?\d+[.:)?]/.test(l))
                .map((l: string) => l.replace(/^\s*[-*]*\s*(?:Q?\d+[?.:)]*\s*)?/, '').trim())
                // Strip markdown bold/italic and inline code
                .map((l: string) => l.replace(/\*{1,3}([^*]+)\*{1,3}/g, '$1').replace(/`([^`]+)`/g, '$1'))
                .filter((l: string) => l.length > 5);
              if (parsed.length > 0) {
                nextQuestions = parsed;
                break;
              }
            }
            // Strip the follow-up section from the answer text (shown as chips instead)
            if (nextQuestions) {
              answer = answer.replace(nqStripRegex, '').trim();
            }

            const aiResponse: AiResponse = {
              answer,
              dsl: dsl || undefined,
              columns: firstResult?.columns,
              rows: firstResult?.rows,
              rowCount: firstResult?.rows?.length,
              error: execResults.some((r) => !r.ok)
                ? execResults
                    .filter((r) => !r.ok)
                    .map((r) => r.output)
                    .join('; ')
                : undefined,
              nextQuestions,
              queryResults: queryResults.length > 1 ? queryResults : undefined,
            };

            callbacks.onEvent({ type: 'complete', data: aiResponse });
            callbacks.onComplete(aiResponse);
            console.log(`[Scout /chat] Complete in ${doneParsed.total_ms}ms`);
            return;
          }
          case 'error': {
            const msg = (parsed.message as string) ?? (parsed.error as string) ?? 'Unknown error';
            callbacks.onEvent({ type: 'error', message: msg });
            callbacks.onError(msg);
            return;
          }
        }
      } catch {
        /* skip malformed event */
      }
    }
  }
}
